import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'cn';
import { Slot } from 'radix-ui';
import * as React from 'react';

/*
 * Variants are limited to the three financial-advisors consumes:
 *   default  <- MUI `contained` (the theme's defaultProps variant)
 *   outline  <- MUI `outlined`
 *   ghost    <- MUI `text`
 * shadcn's destructive, secondary and link variants are deliberately absent:
 * no consumer, and the AC forbids shipping a variant without one.
 *
 * Focus uses the shared RIA outline treatment rather than shadcn's default
 * three-pixel ring and translucent ring colour, so it matches the MuiButtonBase
 * outline the Chromatic baselines were rendered with.
 */
const buttonVariants = cva(
  // 13px and 1.2: Theme/index.ts:517-518; no Tailwind scale match.
  // 0.4px letter spacing: node_modules/@mui/material/styles/createTypography.js:74; no Tailwind scale match.
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-[13px] leading-[1.2] tracking-[0.4px] text-center uppercase whitespace-nowrap transition-colors outline-none focus-visible:focus-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground font-bold duration-150 hover:bg-primary/90',
        outline:
          'border border-input bg-background font-bold duration-150 hover:bg-accent hover:text-accent-foreground',
        ghost: 'font-medium hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'p-4',
        icon: 'size-9',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: true,
    },
  },
);

/*
 * fullWidth defaults to true to match the MUI theme's
 * `defaultProps: { fullWidth: true }` at packages/ui/src/Theme/index.ts:509-514.
 * Every accepted Chromatic baseline was rendered with full-width buttons;
 * defaulting to auto width would diff on every swap. Icon buttons opt out.
 */
function Button({
  className,
  variant = 'default',
  size = 'default',
  fullWidth = size === 'icon' ? false : true,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
